import { Button, Dialog, DialogActions, DialogTitle, IconButton, Stack, Typography, useTheme } from '@mui/material'
import { COLORS } from 'app-constants'
import JelledModal from 'components/JelledModal'
import { TextFieldWithDarkMode } from 'dark-mode-components'
import useCheckMobileScreen from 'hooks/use-check-mobile-screen'
import { useState } from 'react'
import { useSetRecoilState } from 'recoil'
import { toastAtom } from 'state'
import { formatTimestamp, isEmailAddressValid } from 'utils'
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined'
import { useFetchWaitlist } from 'hooks/use-waitlist'
import { WaitlistEntry, WaitlistFeature } from 'api/model'
import { adminCreateWaitlist, adminRemoveWaitlist } from 'api/endpoints/jelledFromFileSpecWithTransformer'
import { LoadingButton } from '@mui/lab'

const AddWaitlistedUserModal = ({
  setWaitlist,
  onClose,
  existingEmails,
}: {
  setWaitlist: React.Dispatch<React.SetStateAction<WaitlistEntry[]>>
  onClose: () => void
  existingEmails: string[]
}) => {
  const setToast = useSetRecoilState(toastAtom)
  const isMobile = useCheckMobileScreen()
  const theme = useTheme()

  const [email, setEmail] = useState('')
  const onEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value)
  }

  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitCount, setSubmitCount] = useState(0)

  const userAlreadyExists = existingEmails.includes(email.toLowerCase())

  const renderEmailHelperText = () => {
    if (submitCount > 0 && !isEmailAddressValid(email)) {
      return 'Please enter a valid email address'
    }

    if (submitCount > 0 && userAlreadyExists) {
      return 'This user is already waitlisted'
    }

    return ''
  }

  const onSaveClick = async () => {
    setSubmitCount(submitCount + 1)

    if (!isEmailAddressValid(email) || userAlreadyExists) {
      return
    }

    setIsSubmitting(true)

    try {
      const response = await adminCreateWaitlist({
        feature: WaitlistFeature.website,
        email,
      })
      setWaitlist((prev) => [...prev, response.data])
      setToast({
        title: 'Successfully added user to waitlist',
        severity: 'success',
      })
      onClose()
    } catch (e) {
      setToast({
        title: 'Failed to add user to waitlist',
        severity: 'error',
      })
    }
  }

  return (
    <JelledModal
      onClose={onClose}
      isOpen={true}
      width={isMobile ? 'calc(100% - 32px)' : '675px'}
      modalStyle={{
        backgroundColor: COLORS.PURE_WHITE,
        marginLeft: isMobile ? '16px' : 0,
        marginRight: isMobile ? '16px' : 0,
      }}>
      <Typography mb={5} variant="h4" color="secondary">
        Add new user to waitlist
      </Typography>

      <TextFieldWithDarkMode
        required
        isDarkBackdrop={false}
        className="has-label"
        theme={theme}
        label="Email"
        placeholder="Add email address"
        value={email}
        onChange={onEmailChange}
        disabled={isSubmitting}
        helperText={renderEmailHelperText()}
        error={submitCount > 0 && (!isEmailAddressValid(email) || !!userAlreadyExists)}
        sx={{
          input: {
            color: 'secondary.main',
          },
          '& ::placeholder': {
            color: 'secondary.main',
          },
        }}
      />

      <Stack direction="row" justifyContent="flex-end" mt={5} spacing={2}>
        <Button
          variant="text"
          color="primary"
          onClick={() => {
            onClose()
          }}>
          Cancel
        </Button>
        <LoadingButton loading={isSubmitting} variant="contained" color="primary" onClick={onSaveClick}>
          Save
        </LoadingButton>
      </Stack>
    </JelledModal>
  )
}

const WaitlistedUserRow = ({
  waitlistEntry,
  setWaitlist,
}: {
  waitlistEntry: WaitlistEntry
  setWaitlist: React.Dispatch<React.SetStateAction<WaitlistEntry[]>>
}) => {
  const setToast = useSetRecoilState(toastAtom)

  const [isHovered, setIsHovered] = useState(false)

  const [isDeleteWaitlistedUserDialogOpen, setIsDeleteWaitlistedUserDialogOpen] = useState(false)

  const [isDeletingWaitlistedUser, setIsDeletingWaitlistedUser] = useState(false)
  const onDeleteWaitlistedUser = async () => {
    if (!waitlistEntry.email) {
      return
    }
    try {
      setIsDeletingWaitlistedUser(true)

      await adminRemoveWaitlist({
        email: waitlistEntry.email,
      })
      setWaitlist((prev) => prev.filter((w) => w.email !== waitlistEntry.email))
      setToast({
        title: 'Successfully deleted waitlisted user',
        severity: 'success',
      })
      setIsDeleteWaitlistedUserDialogOpen(false)
    } catch (e) {
      setToast({
        title: 'Failed to delete waitlisted user',
        severity: 'error',
      })
    } finally {
      setIsDeletingWaitlistedUser(false)
    }
  }

  return (
    <Stack
      direction="row"
      alignItems="center"
      width="100%"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      sx={{
        background: isHovered ? COLORS.GRAY_7 : 'transparent',
        padding: '8px 0',
        height: '40px',
      }}>
      <Typography variant="pS" ml="58px" flex="0 1 350px">
        {waitlistEntry.email}
      </Typography>
      <Typography variant="pS" width="300px">
        {formatTimestamp(waitlistEntry.date_created)}
      </Typography>
      <Typography variant="pS" flex="1">
        N/A
      </Typography>
      {isHovered && (
        <IconButton
          size="small"
          disableRipple={true}
          onClick={() => {
            setIsDeleteWaitlistedUserDialogOpen(true)
          }}
          sx={{
            padding: 0,
            mr: 2,
            color: COLORS.GRAY_3,
          }}>
          <DeleteOutlinedIcon />
        </IconButton>
      )}

      <Dialog open={isDeleteWaitlistedUserDialogOpen}>
        <DialogTitle>Are you sure you want to delete this waitlisted user?</DialogTitle>
        <DialogActions>
          <Button
            onClick={() => {
              setIsDeleteWaitlistedUserDialogOpen(false)
            }}
            disabled={isDeletingWaitlistedUser}>
            Cancel
          </Button>
          <LoadingButton
            loading={isDeletingWaitlistedUser}
            variant="contained"
            color="error"
            onClick={onDeleteWaitlistedUser}
            disabled={isDeletingWaitlistedUser}>
            Delete
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </Stack>
  )
}

const Whitelist = () => {
  const isMobile = useCheckMobileScreen()

  const [isAddWaitlistedUserModalOpen, setIsAddWaitlistedUserModalOpen] = useState(false)

  const { waitlist, isLoading, setWaitlist } = useFetchWaitlist()

  // TODO: add loading state
  if (isLoading) {
    return null
  }

  return (
    <Stack>
      <Stack
        direction={isMobile ? 'column' : 'row'}
        justifyContent="space-between"
        alignItems={isMobile ? 'flex-start' : 'center'}
        mb={2}>
        {!isMobile && (
          <Typography variant="h4" mb={1}>
            Waitlist
          </Typography>
        )}
        <Stack direction="row" alignItems="center" spacing={1}>
          <Button
            variant="contained"
            color="primary"
            onClick={() => {
              setIsAddWaitlistedUserModalOpen(true)
            }}>
            Add new user
          </Button>
        </Stack>
      </Stack>
      <Stack
        p="16px 0"
        gap="16px"
        borderRadius="16px"
        sx={{
          background: COLORS.PURE_WHITE,
        }}>
        <Stack direction="row" alignItems="center" width="100%">
          <Typography variant="h6" ml="58px" flex="0 1 350px">
            Email
          </Typography>
          <Typography variant="h6" width="300px">
            Date created
          </Typography>
          <Typography variant="h6">Account created on</Typography>
        </Stack>

        <Stack
          sx={{
            maxHeight: '80vh',
            overflowY: 'scroll',
          }}>
          {waitlist.length > 0 && (
            <Stack spacing={2}>
              {waitlist.map((waitlistEntry, index) => (
                <WaitlistedUserRow key={index} waitlistEntry={waitlistEntry} setWaitlist={setWaitlist} />
              ))}
            </Stack>
          )}
        </Stack>
      </Stack>

      {isAddWaitlistedUserModalOpen && (
        <AddWaitlistedUserModal
          setWaitlist={setWaitlist}
          existingEmails={waitlist.map((w) => w.email.toLowerCase())}
          onClose={() => setIsAddWaitlistedUserModalOpen(false)}
        />
      )}
    </Stack>
  )
}

export default Whitelist
